import { Box, Paper, Stack } from "@mui/material";
import { Custom, Menu, Popup } from "components";
import { useApp, useService, useSession, useTheme } from "contexts";
import { useNavigate } from "react-router-dom";
import { IPotluck } from "interfaces";
import { Enums, Helpers, Routes } from "utils";
import { Combines } from "helpers";
import { useState } from "react";
import { MessageType } from "types";

type Props = {
  potluck: IPotluck;
  onDelete: (id: string) => void;
};

export const Potluck = ({ potluck, onDelete }: Props) => {
  const navigate = useNavigate();

  const { theme } = useTheme();
  const { updateSettings, user } = useSession();
  const { setFeedback, t } = useApp();
  const { potluckService, userService } = useService();

  const [invitationOpened, setInvitationOpened] = useState<boolean>(false);
  const [confirmationOpened, setConfirmationOpened] = useState<boolean>(false);

  const responses = potluck.menu
    .flatMap((menu) => menu.categories)
    .reduce((acc, current) => {
      const users = current.responses.map((r) => r.user._id);
      users.forEach((u) => !acc.includes(u) && acc.push(u));
      return acc;
    }, []);

  const isActive = new Date(potluck.date) > new Date();
  const isOwner = user.id === potluck.created_by._id;
  const hasCategories = Array.isArray(potluck.menu) &&
    potluck.menu.some((menuItem) => Array.isArray(menuItem.categories) && menuItem.categories.length > 0);

  const handleEdit = () =>
    navigate(
      Helpers.encodedUrl(Routes.managepotluck, {
        id: potluck._id,
      })
    );
  const handleManageInvitation = () => setInvitationOpened(true);
  const handleDelete = () => setConfirmationOpened(true);

  const handleHide = () => {
    userService.addHiddenPotluck(user.id, potluck._id).then((response) => {
      if (response) {
        updateSettings(response.settings);
        setFeedback({
          severity: Enums.EnumFeedback.Success,
          message: t.message.feedback.request_complete_success,
        } as MessageType);
        onDelete?.(potluck._id);
      }
    });
  };

  const handleWithdrawal = async () => {
    potluckService
      .removeParticipants(potluck._id, [user.id])
      .then((response) => {
        if (response) {
          setFeedback({
            severity: Enums.EnumFeedback.Success,
            message: t.message.feedback.request_complete_success,
          } as MessageType);
          onDelete?.(potluck._id);
        }
      });
  };

  const handleOnConfirm = async () => {
    potluckService.delete(potluck._id).then((success) => {
      if (success) {
        setFeedback({
          severity: Enums.EnumFeedback.Success,
          message: t.message.feedback.request_delete_success,
        } as MessageType);

        // TODO : improve it
        onDelete?.(potluck._id);
      } else {
        setFeedback({
          severity: Enums.EnumFeedback.Error,
          message: t.message.feedback.request_error,
        } as MessageType);
      }
    });
    setConfirmationOpened(false);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: 1,
        border: 1,
        height: 380,
        p: theme.spacing.md,
        my: theme.spacing.md,
        backgroundColor: "transparent",
        borderRadius: theme.border.radius,
        borderColor: theme.color.primary.color,
      }}
    >
      <Stack height={1} spacing={theme.spacing.sm}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack>
            <Custom.Typography size={theme.font.md} weight={theme.font.bold}>
              {potluck.party_name}
            </Custom.Typography>
            <Custom.Typography size={theme.font.xs}>
              (
              {Combines.interpolate(t.message.responses, {
                count: responses.length,
              })}
              )
            </Custom.Typography>
          </Stack>
          <Menu.Potluck
            id={potluck._id}
            onHide={handleHide}
            onEdit={handleEdit}
            onWithdrawal={handleWithdrawal}
            onManageInvitation={handleManageInvitation}
            onDelete={handleDelete}
            isActive={isActive}
            isOwner={isOwner}
          />
        </Stack>

        <Stack spacing={theme.spacing.xxs}>
          <Stack direction="row" alignItems="center" spacing={theme.spacing.md}>
            <Custom.Typography size={theme.font.xxs} weight={theme.font.bold}>
              {t.label.when}
            </Custom.Typography>
            <Custom.Typography size={theme.font.sm}>
              {potluck.date}
            </Custom.Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={theme.spacing.md}>
            <Custom.Typography size={theme.font.xxs} weight={theme.font.bold}>
              {t.label.start_time}
            </Custom.Typography>
            <Custom.Typography size={theme.font.sm}>
              {potluck.start_time || t.message.placeholder.dash}
            </Custom.Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={theme.spacing.md}>
            <Custom.Typography size={theme.font.xxs} weight={theme.font.bold}>
              {t.label.end_time}
            </Custom.Typography>
            <Custom.Typography size={theme.font.sm}>
              {potluck.end_time || t.message.placeholder.dash}
            </Custom.Typography>
          </Stack>
        </Stack>
        <Box height={1}>
          <Custom.Typography
            component="pre"
            size={theme.font.xs}
            className="o-ellipsis two"
            sx={{ whiteSpace: "pre-line" }}
          >
            {potluck.message}
          </Custom.Typography>
        </Box>

        <Stack spacing={theme.spacing.sm}>
          <Custom.Button
            variant="contained"
            trailing="right"
            disabled={!isActive || !hasCategories}
            onClick={() =>
              navigate(
                Helpers.encodedUrl(Routes.voting, {
                  id: potluck._id,
                })
              )
            }
          >
            {t.action.vote_now}
          </Custom.Button>
          <Custom.Button
            variant="outlined"
            trailing="right"
            onClick={() =>
              navigate(
                Helpers.encodedUrl(Routes.summary, {
                  id: potluck._id,
                })
              )
            }
          >
            {t.action.view_details}
          </Custom.Button>
        </Stack>
      </Stack>
      <Popup.Confirmation
        open={confirmationOpened}
        onClose={() => setConfirmationOpened(false)}
        onConfirm={handleOnConfirm}
      />
      <Popup.Invitation
        id={potluck._id}
        open={invitationOpened}
        owner={potluck.created_by._id}
        participants={potluck.participants}
        onClose={() => setInvitationOpened(false)}
      />
    </Paper>
  );
};
